import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  Star,
  X,
  AlertCircle,
} from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    currency: 'INR',
    features: ['3D Spatial Floor Plans', 'Modular Kitchen Layout', 'Ambient Lighting Consultation'],
    popular: false,
    status: 'active',
    sort_order: 0,
  });
  const [formSaving, setFormSaving] = useState(false);

  const toast = useToast();

  const loadPackages = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.packages.getAll();
      setPackages(data);
    } catch (err) {
      console.error('Error loading packages:', err);
      setError(err.message || 'Failed to fetch packages from Supabase.');
      toast.error('Failed to load packages: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedPackage(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      currency: 'INR',
      features: [
        'Complete 3D Architecture Visualizer',
        'Custom Modular Kitchen Setup',
        'Premium Wardrobe & Storage Units',
        'Turnkey Material Sourcing Guarantee',
      ],
      popular: false,
      status: 'active',
      sort_order: packages.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (pkg) => {
    setIsEditing(true);
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name,
      slug: pkg.slug,
      description: pkg.description || '',
      price: pkg.price || '',
      currency: pkg.currency || 'INR',
      features: Array.isArray(pkg.features) && pkg.features.length > 0 ? pkg.features : [''],
      popular: Boolean(pkg.popular),
      status: pkg.status || 'active',
      sort_order: pkg.sort_order || 0,
    });
    setModalOpen(true);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    if (!isEditing || !formData.slug) {
      const autoSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, name, slug: autoSlug }));
    } else {
      setFormData((prev) => ({ ...prev, name }));
    }
  };

  // Feature list handlers
  const handleFeatureChange = (index, value) => {
    const updated = [...formData.features];
    updated[index] = value;
    setFormData({ ...formData, features: updated });
  };

  const handleAddFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const handleRemoveFeature = (index) => {
    const updated = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updated });
  };

  // Quick 1-click status toggle
  const handleToggleStatus = async (pkg, e) => {
    e.stopPropagation();
    try {
      await queries.packages.toggleStatus(pkg.id, pkg.status);
      toast.success(`Package status set to ${pkg.status === 'active' ? 'Inactive' : 'Active'}`);
      loadPackages();
    } catch (err) {
      toast.error('Failed to toggle package: ' + err.message);
    }
  };

  // Quick 1-click popular badge toggle
  const handleTogglePopular = async (pkg, e) => {
    e.stopPropagation();
    try {
      await queries.packages.togglePopular(pkg.id, pkg.popular);
      toast.success(`Package ${!pkg.popular ? 'highlighted as Popular' : 'unmarked from Popular'}`);
      loadPackages();
    } catch (err) {
      toast.error('Failed to toggle popular badge: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Package name is required');
      return;
    }

    setFormSaving(true);
    try {
      const cleanFeatures = formData.features.filter((f) => f && f.trim().length > 0);

      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        slug: formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        features: cleanFeatures,
      };

      if (isEditing && selectedPackage) {
        await queries.packages.update(selectedPackage.id, payload);
        toast.success('Package updated in Supabase');
      } else {
        await queries.packages.create(payload);
        toast.success('Package created in Supabase');
      }

      setModalOpen(false);
      loadPackages();
    } catch (err) {
      console.error('Error saving package:', err);
      toast.error('Failed to save package: ' + err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPackage) return;
    try {
      await queries.packages.delete(selectedPackage.id);
      toast.success('Package deleted from Supabase');
      setConfirmOpen(false);
      setSelectedPackage(null);
      loadPackages();
    } catch (err) {
      console.error('Error deleting package:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const formatPrice = (price, currency = 'INR') => {
    if (!price) return 'Custom Quote';
    const num = Number(price);
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol} ${num.toLocaleString('en-IN')}`;
  };

  const columns = [
    {
      header: 'Package Name',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] border border-[#9BB7A3]/20 flex items-center justify-center shrink-0">
            <Package size={20} />
          </div>
          <div>
            <div className="font-bold text-sm text-slate-100 flex items-center gap-2">
              <span>{row.name}</span>
              {row.popular && (
                <span className="p-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20" title="Popular Tier">
                  <Star size={11} className="fill-amber-400" />
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 truncate max-w-xs">{row.description}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Price',
      accessor: (row) => (
        <span className="font-bold text-sm text-slate-100 font-mono">
          {formatPrice(row.price, row.currency)}
        </span>
      ),
    },
    {
      header: 'Features',
      accessor: (row) => (
        <span className="text-xs text-slate-400">
          {Array.isArray(row.features) ? `${row.features.length} inclusions` : '0 inclusions'}
        </span>
      ),
    },
    {
      header: 'Status (Click to Toggle)',
      accessor: (row) => (
        <button
          onClick={(e) => handleToggleStatus(row, e)}
          className="group focus:outline-none transition active:scale-95"
          title="Click to toggle Active / Inactive"
        >
          <Badge variant={row.status === 'active' ? 'success' : 'default'}>
            {row.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        </button>
      ),
    },
    {
      header: 'Actions',
      className: 'text-right',
      accessor: (row) => (
        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => handleTogglePopular(row, e)}
            className={`p-2 rounded-xl transition ${
              row.popular
                ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20'
                : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800'
            }`}
            title={row.popular ? 'Highlighted Package' : 'Mark as Most Popular'}
          >
            <Star size={15} className={row.popular ? 'fill-amber-400' : ''} />
          </button>
          <button
            onClick={() => handleOpenEdit(row)}
            className="p-2 rounded-xl text-slate-400 hover:text-[#9BB7A3] hover:bg-[#9BB7A3]/10 transition"
            title="Edit Package"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={() => {
              setSelectedPackage(row);
              setConfirmOpen(true);
            }}
            className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 transition"
            title="Delete Package"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <Package size={24} className="text-[#9BB7A3]" />
            Pricing Packages Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Configure transparent studio service tiers, price values, and feature bullet point inclusions.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Add Package</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Main DataTable */}
      <DataTable
        columns={columns}
        data={packages}
        loading={loading}
        searchKey="name"
        searchPlaceholder="Search packages by title..."
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ],
          },
        ]}
        emptyIcon={<Package size={40} className="text-slate-600" />}
        emptyTitle="No Packages Found"
        emptyDescription="Create your first pricing package to display on your public website."
        onAddNew={handleOpenCreate}
        addNewLabel="Add Package"
      />

      {/* Create / Edit Package Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !formSaving && setModalOpen(false)}
        title={isEditing ? 'Edit Package' : 'Create Pricing Package'}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Package Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={handleNameChange}
                placeholder="e.g., Premium 3BHK Architecture Suite"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                URL Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="premium-3bhk-architecture-suite"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Price
              </label>
              <input
                type="number"
                step="any"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="e.g. 150000"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="active">Active (Visible)</option>
                <option value="inactive">Inactive (Hidden)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Package Subtitle / Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ideal for luxury apartments, 3BHK villas, and modern renovations."
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          {/* Feature Bullets Builder */}
          <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                Inclusions & Deliverables ({formData.features.length})
              </label>
              <button
                type="button"
                onClick={handleAddFeature}
                className="inline-flex items-center gap-1 text-xs text-[#9BB7A3] hover:underline font-semibold"
              >
                <Plus size={14} /> Add Inclusion Bullet
              </button>
            </div>

            <div className="space-y-2">
              {formData.features.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono w-4">{idx + 1}.</span>
                  <input
                    type="text"
                    value={feat}
                    onChange={(e) => handleFeatureChange(idx, e.target.value)}
                    placeholder="e.g. Modular Kitchen 3D Layout & Fabrication"
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(idx)}
                    className="p-2 text-slate-500 hover:text-rose-400 transition"
                    title="Remove Bullet"
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Flag */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="popular"
              checked={formData.popular}
              onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
              className="w-4 h-4 rounded border-slate-800 text-[#9BB7A3] focus:ring-[#9BB7A3] bg-slate-950 cursor-pointer"
            />
            <label htmlFor="popular" className="text-xs text-slate-300 font-semibold cursor-pointer">
              Mark as "Most Popular" / Recommended Package
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formSaving}
              className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50"
            >
              {formSaving ? 'Saving to Database...' : isEditing ? 'Update Package' : 'Create Package'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedPackage(null);
        }}
        onConfirm={handleDelete}
        title="Delete Package"
        message={`Are you sure you want to delete package "${selectedPackage?.name}"?`}
        confirmText="Delete Package"
        type="danger"
      />
    </div>
  );
}
